import type { ReactNode } from 'react'
import { Toaster as Sonner, toast as sonnerToast } from 'sonner'
import { useTheme } from '@/theme/ThemeProvider'

/** Id sonner assigns a toast. Keep it only if you need to dismiss that toast. */
export type ToastId = string | number

/**
 * The toast calls this app makes. Sonner's `toast.promise` is generic over
 * `any`; that default stays in the library instead of on our export.
 */
export interface ToastApi {
  (message: ReactNode): ToastId
  success: (message: ReactNode) => ToastId
  error: (message: ReactNode) => ToastId
}

function showToast(message: ReactNode): ToastId {
  return sonnerToast(message)
}

export const toast: ToastApi = Object.assign(showToast, {
  success: (message: ReactNode): ToastId => sonnerToast.success(message),
  error: (message: ReactNode): ToastId => sonnerToast.error(message),
})

/**
 * Brief confirmations for quick background actions (invite sent, role
 * changed). Errors and form problems stay inline next to what failed.
 */
export function Toaster() {
  const { resolvedTheme } = useTheme()
  return (
    <Sonner
      theme={resolvedTheme}
      position="bottom-right"
      duration={3500}
      // Colors, border, and radius come from the tokens via [data-sonner-toaster] in index.css.
      toastOptions={{
        classNames: {
          toast: 'shadow-overlay text-body',
          description: 'text-muted-foreground',
          actionButton: 'bg-primary text-primary-foreground',
        },
      }}
    />
  )
}
